/**
 * Validacao de documentos brasileiros e ranges de data.
 *
 * Modulo separado de FormatFunctions.tsx (que so formata strings) pra
 * evitar mistura de "format" com "validate" e simplificar tests.
 */

import {
  addDays,
  formatISO,
  isAfter,
  isBefore,
  isValid,
  parseISO,
  startOfDay,
  startOfToday,
} from 'date-fns';

// === CNPJ ===

const FIRST_WEIGHTS = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
const SECOND_WEIGHTS = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

/**
 * Mascara do campo de CNPJ.
 *
 * ⚠️ `*` = alfanumerico e `9` = digito, no vocabulario do `react-input-mask`.
 * As 12 primeiras posicoes sao `*` porque aceitam letra desde a IN RFB
 * 2.229/2024; os 2 DVs seguem `9`.
 *
 * Existe como CONSTANTE porque a mascara estava escrita a mao em cada campo, e
 * validador permissivo com mascara restritiva nao adianta nada: com
 * `99.999.999/9999-99` o operador **nao consegue digitar** a letra, e o fix da
 * validacao fica inerte.
 */
export const CNPJ_MASK = '**.***.***/****-99';

/**
 * Valor de um caractere no calculo do DV: **codigo ASCII menos 48**.
 *
 * E a regra do CNPJ alfanumerico (IN RFB 2.229/2024). Ela GENERALIZA a antiga
 * em vez de substituir: '0' vale 48-48=0 e '9' vale 57-48=9, exatamente como
 * antes; as letras continuam a serie, 'A'=17 ate 'Z'=42. Por isso um CNPJ
 * puramente numerico valida igual ao que validava, sem caso especial.
 */
const valorDoCaractere = (c: string): number => c.charCodeAt(0) - 48;

const calcDV = (base: string, weights: number[]): number => {
  let sum = 0;
  for (let i = 0; i < weights.length; i += 1) {
    sum += valorDoCaractere(base[i]) * weights[i];
  }
  const r = sum % 11;
  return r < 2 ? 0 : 11 - r;
};

// Aceita CNPJ com ou sem pontuacao. Rejeita all-same (passa o modulo 11
// trivialmente mas nao e CNPJ real).
//
// ⚠️ ALFANUMERICO: as 12 primeiras posicoes aceitam letra desde a IN RFB
// 2.229/2024; **os 2 DVs continuam numericos**. Antes daqui a normalizacao era
// `replace(/\D/g,'')`, que APAGAVA as letras — sobravam menos de 14 caracteres
// e a funcao devolvia false. O efeito nao ficava so na legalizacao: este mesmo
// validador gateia o wizard de contratos societarios (`schema.ts` e o
// `ContratoVigenteStep`), entao empresa com CNPJ novo nao passava do formulario.
// O PR que consta como "CNPJ alfanumerico resolvido" (#594) cobriu **filtros e
// busca**, nunca a validacao de formulario.
export const isValidCNPJ = (value: string | null | undefined): boolean => {
  if (!value) return false;
  // Mantem letra e digito, descarta pontuacao. Caixa alta porque a Receita
  // canoniza em maiuscula e o operador pode digitar minuscula.
  const cnpj = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  if (cnpj.length !== 14) return false;
  // 12 alfanumericas + 2 DVs NUMERICOS.
  if (!/^[A-Z0-9]{12}\d{2}$/.test(cnpj)) return false;
  if (/^([A-Z0-9])\1{13}$/.test(cnpj)) return false;
  const dv1 = calcDV(cnpj.slice(0, 12), FIRST_WEIGHTS);
  if (Number(cnpj[12]) !== dv1) return false;
  const dv2 = calcDV(cnpj.slice(0, 13), SECOND_WEIGHTS);
  if (Number(cnpj[13]) !== dv2) return false;
  return true;
};

// === NIRE ===
// JUCEG (Goias) aceita 11 ou 12 digitos historicamente. Outras juntas
// usam 11 fixos. Range 11-12 cobre Rede + maioria nacional.
export const NIRE_REGEX = /^\d{11,12}$/;

export const isValidNire = (value: string | null | undefined): boolean => {
  if (!value) return false;
  return NIRE_REGEX.test(value);
};

// === Date range ===

export type DateRangeBound = Date | string;

const TODAY_OFFSET_RE = /^today([+-]\d+)?$/;
const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

const resolveBound = (b: DateRangeBound | undefined): Date | undefined => {
  if (b == null) return undefined;
  if (b instanceof Date) return startOfDay(b);
  const offsetMatch = TODAY_OFFSET_RE.exec(b);
  if (offsetMatch) {
    const offset = offsetMatch[1] ? Number(offsetMatch[1]) : 0;
    return addDays(startOfToday(), offset);
  }
  const parsed = parseISO(b);
  if (!isValid(parsed)) return undefined;
  return startOfDay(parsed);
};

const formatBound = (b: DateRangeBound | undefined): string => {
  const r = resolveBound(b);
  if (!r) return '';
  return formatISO(r, { representation: 'date' });
};

export interface DateRangeOptions {
  min?: DateRangeBound;
  max?: DateRangeBound;
  message?: string;
  name?: string;
}

// Retorna um objeto compativel com yup `.test(config)`. Aceita strings
// "today" e "today+N" / "today-N" pra range relativo (ex: hoje+30 dias).
export const dateRangeTest = (opts: DateRangeOptions) => {
  const { min, max, message, name = 'data-range' } = opts;

  const test = (value: string | null | undefined): boolean => {
    if (!value) return true; // .required() trata vazio
    const m = ISO_DATE_RE.exec(value);
    if (!m) return false;
    const year = Number(m[1]);
    const month = Number(m[2]);
    const day = Number(m[3]);
    // Pydantic v2 date aceita 1-9999. Nativo <input type="date">
    // pode permitir 5+ digitos em alguns browsers — bloqueamos aqui.
    if (year < 1 || year > 9999) return false;
    const date = new Date(year, month - 1, day);
    // Detecta dias invalidos (ex: 2026-02-30 vira 2026-03-02 silenciosamente).
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
      return false;
    }
    const minD = resolveBound(min);
    const maxD = resolveBound(max);
    if (minD && isBefore(date, minD)) return false;
    if (maxD && isAfter(date, maxD)) return false;
    return true;
  };

  return {
    name,
    message:
      message ?? `Data deve estar entre ${formatBound(min) || '—'} e ${formatBound(max) || '—'}`,
    test,
  };
};
